import { HTMLAttributes, forwardRef, CSSProperties } from 'react';
import { CoreIcon } from '@autopass-web/mochi-icons';
import styles from './index.module.sass';
import CoreInput from '../core-input/core-input';
import classnames from 'classnames/bind';
import iconClose from '../../../../domain/assets/close.svg';

const cx = classnames.bind(styles);

interface FormInputProps {
  autocomplete?: string;
  appearance: 'normal' | 'round' | 'grey';
  label?: string;
  labelFor?: string;
  type?: string;
  dataType?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  className?: any;
  placeholder?: string;
  behindValue?: any;
  value: string;
  digits?: string;
  maxLength?: number;
  allCaps?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  errorMessageFloating?: boolean;
  style?: CSSProperties;
  onTextChange?: (string: string) => void;
  onFocus?(): void;
  onBlur?(): void;
  onClear?(): void;
  onKeyPressEnter?(): void;
  onKeyDown?(): void;
  inputButton?(): void;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      appearance = 'normal',
      className,
      label = '',
      labelFor,
      type = 'text',
      dataType,
      inputMode,
      placeholder,
      behindValue = '',
      value = '',
      digits,
      maxLength,
      errorMessage = '',
      errorMessageFloating,
      allCaps = false,
      onTextChange,
      onFocus,
      onBlur,
      onKeyPressEnter,
      onClear,
      onKeyDown,
      disabled,
      style,
      inputButton,
      autocomplete,
    },
    ref,
  ) => {
    const Error = (): JSX.Element => {
      if (!errorMessage) return <></>;

      const errorMessageClass = cx({
        'error-message': true,
        floating: errorMessageFloating,
      });
      return <div className={errorMessageClass}>{errorMessage}</div>;
    };

    const containerClass = cx({
      container: true,
      error: errorMessage !== '',
      disabled,
      [appearance]: true,
    });

    return (
      <div className={styles['form-input']} style={style}>
        {label && (
          <label className={styles.label} htmlFor={labelFor}>
            {label}
          </label>
        )}
        <div className={`${containerClass} ${className}`}>
          {behindValue && <div className={styles.behind}>{behindValue}</div>}
          <CoreInput
            autocomplete={autocomplete}
            behindValue={behindValue}
            inputButton={inputButton}
            ref={ref}
            labelFor={labelFor}
            dataType={dataType}
            disabled={disabled}
            className={styles.input}
            type={type}
            inputmode={inputMode}
            placeholder={placeholder}
            value={value}
            digits={digits}
            maxLength={maxLength}
            allCaps={allCaps}
            onTextChange={onTextChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyPressEnter={onKeyPressEnter}
            onKeyDown={onKeyDown}
          />
          {onClear && value && (
            <CoreIcon
              src={iconClose}
              className={styles.clear}
              onClick={onClear}
            />
          )}
        </div>
        <Error />
      </div>
    );
  },
);

export default FormInput;
